import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { TableProps } from 'antd'
import { Flex, Table } from 'antd'
import { format, isValid, parseISO } from 'date-fns'
import { SeedInventoryApiRecord, SeedInventoryListMeta, SeedPedigree } from 'modules/domain/seedTracker/types'
import { listSeedInventoryByProductCard, SeedInventoryListFilters } from 'modules/domain/seedTracker/managers'
import { formatPhoneForDisplay } from 'modules/utils/formatPhoneForDisplay'
import { PedigreeTag } from './PedigreeTag'
import { formatMt, formatMtAsNegative } from '../formatMt'
import {
  filterActionBarStyles,
  scrollAreaStyle,
  summaryFooterWrapStyle,
  summaryLabelStyle,
  summaryValueStyle,
  tableRootFlexStyle,
} from '../styles'

// Seed Inventory data type (exported for parent/drawer)
export type SeedInventory = {
  id: string
  companyId: string
  grower: string
  year: string
  seasonYear: string
  availableMT: string
  pendingSaleMT: string
  disposedMT: string
  onHandMT: string
  cleanYieldActualMT: string
  cleanYieldEstimateMT: string
  grossYieldActualBU: string
  grossYieldEstimateBU: string
  initialGermActual: string
  initialGermEstimate: string
  finalGermActual: string
  finalGermEstimate: string
  diseasedActual: string
  diseasedEstimate: string
  plannedSeededArea: string
  currentSeededAreaActual: string
  currentSeededAreaEstimate: string
  seededPedigree: SeedPedigree
  projectedPedigree: SeedPedigree
  projectedYieldActualBU: string
  projectedYieldEstimateBU: string
  lastUpdatedDate: string
  cropCertificateId: string
  location: string
  region: string
  contactName: string
  contactNumber: string
  contactEmail: string
}

const emptyMetric = '—'

function s(v: string | null | undefined): string {
  if (v == null || v === '') {
    return emptyMetric
  }
  return v
}

const pedigreeOrder = Object.values(SeedPedigree)

function getSeasonYear(createdAt?: string | null): string {
  if (!createdAt) {
    return emptyMetric
  }

  const date = new Date(createdAt)
  return isValid(date) ? String(date.getFullYear()) : emptyMetric
}

function formatSeedInventoryDate(value: string): string {
  if (!value) {
    return emptyMetric
  }

  const parsed = parseISO(value)
  const date = isValid(parsed) ? parsed : new Date(value)
  return isValid(date) ? format(date, 'dd MMM yyyy') : value
}

function compareByGrower(a: SeedInventory, b: SeedInventory): number {
  const growerCmp = a.grower.localeCompare(b.grower, undefined, { sensitivity: 'base', numeric: true })
  if (growerCmp !== 0) {
    return growerCmp
  }

  const aPedigreeOrder = pedigreeOrder.indexOf(a.seededPedigree)
  const bPedigreeOrder = pedigreeOrder.indexOf(b.seededPedigree)
  if (aPedigreeOrder !== -1 && bPedigreeOrder !== -1) {
    return aPedigreeOrder - bPedigreeOrder
  }

  return 0
}

export function mapSeedInventoryRecordToSeedInventoryRow(r: SeedInventoryApiRecord): SeedInventory {
  const grower = r.company?.official_name ?? emptyMetric
  return {
    id: r.id,
    companyId: r.company_id,
    grower,
    year: s(r.year),
    seasonYear: getSeasonYear(r.created_at),
    availableMT: s(r.available_mt),
    pendingSaleMT: s(r.pending_sale_mt),
    disposedMT: s(r.disposed_mt),
    onHandMT: s(r.on_hand_mt),
    cleanYieldActualMT: s(r.clean_yield_actual_mt),
    cleanYieldEstimateMT: s(r.clean_yield_estimate_mt),
    grossYieldActualBU: s(r.gross_yield_actual_bu),
    grossYieldEstimateBU: s(r.gross_yield_estimate_bu),
    initialGermActual: s(r.initial_germ_actual),
    initialGermEstimate: s(r.initial_germ_estimate),
    finalGermActual: s(r.final_germ_actual),
    finalGermEstimate: s(r.final_germ_estimate),
    diseasedActual: s(r.diseased_actual),
    diseasedEstimate: s(r.diseased_estimate),
    plannedSeededArea: s(r.planned_seeded_area),
    currentSeededAreaActual: s(r.current_seeded_area_actual),
    currentSeededAreaEstimate: s(r.current_seeded_area_estimate),
    seededPedigree: r.seeded_pedigree,
    projectedPedigree: r.projected_pedigree,
    projectedYieldActualBU: s(r.projected_yield_actual_bu),
    projectedYieldEstimateBU: s(r.projected_yield_estimate_bu),
    lastUpdatedDate: formatSeedInventoryDate(r.updated_at ?? r.last_updated_date ?? ''),
    cropCertificateId: s(r.crop_certificate_id),
    location: emptyMetric,
    region: s(r.region),
    contactName: s(r.contact_name),
    contactNumber: s(formatPhoneForDisplay(r.contact_number)),
    contactEmail: s(r.contact_email),
  }
}

type SeedInventoryTableProps = {
  onRowClick?: (record: SeedInventory) => void
  /** When true, only Grower and Pedigree columns are shown (for split-panel layout). */
  compactColumns?: boolean
  /** Id of the selected row; used to highlight the active row when compact. */
  selectedRecordId?: string | null
  /** When true, row switching is blocked except for the selected row. */
  lockRowSelection?: boolean
  /** Search + filters controls rendered on the left of the filter row. */
  filterBarLeft?: React.ReactNode
  /** Product card id from the seed tracker row; filters GET /seed_inventory. */
  productCardId?: string
  /** When provided, the table auto-selects the first row for this company once data loads. */
  initialSelectedCompanyId?: string
  /** Increment to force reloading table data from API. */
  refreshKey?: number
  /** Optional grower (company) filter. */
  companyId?: string
  /** Optional seeded pedigree filter. */
  seededPedigree?: string
  /** Optional region filter. */
  regionId?: string
  /** Optional created-at filter year. */
  year?: string
}

const allColumns: TableProps<SeedInventory>['columns'] = [
  {
    title: 'Grower',
    dataIndex: 'grower',
    key: 'grower',
  },
  {
    title: 'Pedigree',
    dataIndex: 'seededPedigree',
    key: 'seededPedigree',
    render: (pedigree: SeedPedigree) => <PedigreeTag pedigree={pedigree} />,
  },
  {
    title: 'Land (Acres)',
    dataIndex: 'currentSeededAreaActual',
    key: 'currentSeededAreaActual',
  },
  {
    title: 'Available (MT)',
    dataIndex: 'availableMT',
    key: 'availableMT',
    render: (value: string) => formatMt(value),
  },
  {
    title: 'Pending Sale (MT)',
    dataIndex: 'pendingSaleMT',
    key: 'pendingSaleMT',
    render: (value: string) => formatMtAsNegative(value),
  },
  {
    title: 'Disposed (MT)',
    dataIndex: 'disposedMT',
    key: 'disposedMT',
    render: (value: string) => formatMtAsNegative(value),
  },
  {
    title: 'On Hand (MT)',
    dataIndex: 'onHandMT',
    key: 'onHandMT',
  },
  {
    title: 'Target Clean Yield (MT)',
    dataIndex: 'cleanYieldEstimateMT',
    key: 'cleanYieldEstimateMT',
  },
  {
    title: 'Actual Clean Yield (MT)',
    dataIndex: 'cleanYieldActualMT',
    key: 'cleanYieldActualMT',
  },
  {
    title: 'Gross Yield (Bu)',
    dataIndex: 'grossYieldActualBU',
    key: 'grossYieldActualBU',
  },
  {
    title: 'Initial Germ (%)',
    dataIndex: 'initialGermActual',
    key: 'initialGermActual',
  },
  {
    title: 'Final Germ (%)',
    dataIndex: 'finalGermActual',
    key: 'finalGermActual',
  },
  {
    title: 'Diseased (%)',
    dataIndex: 'diseasedActual',
    key: 'diseasedActual',
  },
  {
    title: 'Last Updated Date',
    dataIndex: 'lastUpdatedDate',
    key: 'lastUpdatedDate',
    render: (value: string) => formatSeedInventoryDate(value),
  },
]

const compactColumnKeys = ['grower', 'seededPedigree'] as const
const compactColumns = allColumns.filter(
  (col) => col.key && compactColumnKeys.includes(col.key as (typeof compactColumnKeys)[number]),
)

const totalColumnKeys = [
  'currentSeededAreaActual',
  'availableMT',
  'pendingSaleMT',
  'disposedMT',
  'onHandMT',
  'cleanYieldEstimateMT',
  'cleanYieldActualMT',
  'grossYieldActualBU',
  'initialGermActual',
  'finalGermActual',
  'diseasedActual',
] as const

const averageColumnKeys = ['initialGermActual', 'finalGermActual', 'diseasedActual'] as const

type SeedInventoryTotalColumnKey = (typeof totalColumnKeys)[number]
type SeedInventoryTableTotals = Partial<Record<SeedInventoryTotalColumnKey, string>>

const summaryCellWrapStyle: React.CSSProperties = {
  minHeight: 40,
}

function toFooterValue(v: string | null | undefined): string {
  if (v == null || v === '') {
    return emptyMetric
  }
  return v
}

function mapMetaTotalsToTableTotals(meta?: SeedInventoryListMeta): SeedInventoryTableTotals {
  const totals = meta?.totals
  return {
    currentSeededAreaActual: toFooterValue(totals?.current_seeded_area_actual),
    availableMT: toFooterValue(totals?.available_mt),
    pendingSaleMT: toFooterValue(totals?.pending_sale_mt),
    disposedMT: toFooterValue(totals?.disposed_mt),
    onHandMT: toFooterValue(totals?.on_hand_mt),
    cleanYieldEstimateMT: toFooterValue(totals?.clean_yield_estimate_mt),
    cleanYieldActualMT: toFooterValue(totals?.clean_yield_actual_mt),
    grossYieldActualBU: toFooterValue(totals?.gross_yield_actual_bu),
    initialGermActual: toFooterValue(totals?.initial_germ_actual),
    finalGermActual: toFooterValue(totals?.final_germ_actual),
    diseasedActual: toFooterValue(totals?.diseased_actual),
  }
}

function getColumnKey(column: NonNullable<TableProps<SeedInventory>['columns']>[number], index: number): string {
  if (column.key != null) {
    return String(column.key)
  }
  if ('dataIndex' in column && column.dataIndex != null) {
    return Array.isArray(column.dataIndex) ? column.dataIndex.join('.') : String(column.dataIndex)
  }
  return String(index)
}

function getSummaryLabel(key: SeedInventoryTotalColumnKey): 'Average' | 'Total' {
  return averageColumnKeys.includes(key as (typeof averageColumnKeys)[number]) ? 'Average' : 'Total'
}

const SeedInventoryTable: React.FC<SeedInventoryTableProps> = ({
  onRowClick,
  compactColumns: useCompactColumns = false,
  selectedRecordId,
  lockRowSelection = false,
  filterBarLeft,
  productCardId,
  initialSelectedCompanyId,
  refreshKey,
  companyId,
  seededPedigree,
  regionId,
  year,
}) => {
  const [data, setData] = useState<SeedInventory[]>([])
  const [totals, setTotals] = useState<SeedInventoryTableTotals>({})
  const [loading, setLoading] = useState(false)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const tableWrapRef = useRef<HTMLDivElement>(null)
  const autoSelectedCompanyIdRef = useRef<string | null>(null)
  const filters: SeedInventoryListFilters = {
    companyId: companyId || undefined,
    seededPedigree: seededPedigree || undefined,
    regionId: regionId || undefined,
    year: year || undefined,
  }

  useEffect(() => {
    if (!productCardId) {
      setData([])
      setTotals({})
      return
    }
    let cancelled = false
    setLoading(true)
    listSeedInventoryByProductCard(productCardId, 1, 500, filters)
      .then((res) => {
        if (cancelled) {
          return
        }
        const rows = (res.data ?? []).map(mapSeedInventoryRecordToSeedInventoryRow)
        setData(rows.sort(compareByGrower))
        setTotals(mapMetaTotalsToTableTotals(res.meta))
      })
      .catch(() => {
        if (!cancelled) {
          setData([])
          setTotals({})
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [productCardId, refreshKey, companyId, seededPedigree, regionId, year]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!initialSelectedCompanyId || !onRowClick || lockRowSelection) {
      return
    }

    if (autoSelectedCompanyIdRef.current === initialSelectedCompanyId) {
      return
    }

    const matchingRow = data.find((record) => record.companyId === initialSelectedCompanyId)
    if (!matchingRow) {
      return
    }

    autoSelectedCompanyIdRef.current = initialSelectedCompanyId
    onRowClick(matchingRow)
  }, [data, initialSelectedCompanyId, lockRowSelection, onRowClick])

  useEffect(() => {
    autoSelectedCompanyIdRef.current = null
  }, [initialSelectedCompanyId, productCardId, refreshKey])

  const columns = useMemo(() => (useCompactColumns ? compactColumns : allColumns), [useCompactColumns])

  const rowKey: TableProps<SeedInventory>['rowKey'] = (record) => record.id

  const showFilterRow = filterBarLeft != null
  const showSummary = !useCompactColumns && data.length > 0

  useLayoutEffect(() => {
    if (useCompactColumns) {
      setColumnWidths({})
      return
    }

    const root = tableWrapRef.current
    if (!root) {
      return
    }

    const measure = () => {
      const headerCells = root.querySelectorAll<HTMLTableCellElement>('.ant-table-thead th')
      if (!headerCells.length) {
        return
      }
      const widths: Record<string, number> = {}
      columns?.forEach((column, index) => {
        const key = getColumnKey(column, index)
        const width = headerCells[index]?.getBoundingClientRect().width
        if (width && width > 0) {
          widths[key] = width
        }
      })
      setColumnWidths(widths)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(root)
    window.addEventListener('resize', measure)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [columns, data.length, useCompactColumns])

  const footerColumns = useMemo<TableProps<SeedInventory>['columns']>(() => {
    return columns?.map((column, index) => {
      const key = getColumnKey(column, index)
      const isTotalColumn = totalColumnKeys.includes(key as SeedInventoryTotalColumnKey)
      let value = isTotalColumn ? totals[key as SeedInventoryTotalColumnKey] ?? emptyMetric : ''
      if (key === 'availableMT') {
        value = formatMt(value)
      } else if (key === 'pendingSaleMT') {
        value = formatMtAsNegative(value)
      } else if (key === 'disposedMT') {
        value = formatMtAsNegative(value)
      } else if (key === 'grossYieldActualBU') {
        value = formatMt(value)
      }
      const label = isTotalColumn ? getSummaryLabel(key as SeedInventoryTotalColumnKey) : ''

      return {
        ...column,
        width: columnWidths[key] ?? column.width,
        render: () =>
          isTotalColumn ? (
            <Flex vertical style={summaryCellWrapStyle}>
              <span style={summaryLabelStyle}>{label}</span>
              <span style={summaryValueStyle}>{value}</span>
            </Flex>
          ) : (
            ''
          ),
      }
    })
  }, [columnWidths, columns, totals])

  return (
    <Flex vertical style={tableRootFlexStyle}>
      {showFilterRow && (
        <Flex vertical style={filterActionBarStyles}>
          <Flex justify="space-between" align="center" flex={1} gap="middle" wrap="wrap">
            <Flex gap="middle" flex={1} align="center" wrap="wrap">
              {filterBarLeft}
            </Flex>
          </Flex>
        </Flex>
      )}
      {showSummary && (
        <div style={summaryFooterWrapStyle}>
          <Table<SeedInventory>
            columns={footerColumns}
            dataSource={[{} as SeedInventory]}
            rowKey={() => 'seed-inventory-summary-row'}
            showHeader={false}
            pagination={false}
          />
        </div>
      )}
      <div style={scrollAreaStyle} ref={tableWrapRef}>
        <Table<SeedInventory>
          columns={columns}
          dataSource={data}
          rowKey={rowKey}
          loading={loading}
          onRow={
            onRowClick
              ? (record) => ({
                  onClick: () => {
                    if (!lockRowSelection || !selectedRecordId || record.id === selectedRecordId) {
                      onRowClick(record)
                    }
                  },
                  style: {
                    cursor:
                      !lockRowSelection || !selectedRecordId || record.id === selectedRecordId
                        ? 'pointer'
                        : 'not-allowed',
                  },
                })
              : undefined
          }
          rowClassName={(record) =>
            selectedRecordId && record.id === selectedRecordId ? 'ant-table-row-selected' : ''
          }
          pagination={{
            showSizeChanger: false,
            position: ['bottomLeft'],
          }}
        />
      </div>
    </Flex>
  )
}

export default SeedInventoryTable
